import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Organisation } from '../../../api';
import OrganisationsTable from './OrganisationsTable';
import CreateOrganisationDialog from './CreateOrganisationDialog/CreateOrganisationDialog';

interface OrganisationsPageProps {
  organisations: Organisation[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onDelete: (organisationId: string) => void;
  onViewDetails: (organisationId: string) => void;
  onRefresh: () => void;
}

const OrganisationsPage: React.FC<OrganisationsPageProps> = ({
  organisations,
  loading,
  error,
  searchQuery,
  onSearchChange,
  onDelete,
  onViewDetails,
  onRefresh,
}) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight={700}>
          Organisations
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Organisation
          </Button>
        </Box>
      </Box>
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search organisations by name, domain, or email..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {organisations.length} organisation{organisations.length !== 1 ? 's' : ''}
          </Typography>
          <OrganisationsTable
            organisations={organisations}
            onDelete={onDelete}
            onViewDetails={onViewDetails}
          />
        </>
      )}
      <CreateOrganisationDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={onRefresh}
      />
    </Box>
  );
};

export default OrganisationsPage;
